/**
 * CR-6441: customer-view layout for the Purchasing → ASN filter panel.
 *
 * Regroups the existing filters into the five sections from the supplied
 * design — Date Range · Company · Supplier · Classification · Order Details —
 * matching the Dashboard filter styling with shaded section headers.
 *
 * CR-6449: the supplier view now lives in `ASNFilterSupplierLayout`, and the
 * panel shell (header actions, Clear all / Apply filters footer) is rendered
 * once by `ASNFilterSection` for both views, so this component is the customer
 * section body only. Its fields, order and cascades are unchanged.
 *
 * Client review (Sep 2026): sections are separated by the same inset divider
 * the Deliveries tab uses (padded on both sides, not edge to edge), every
 * label is Title Case, and Supplier Type (which the client reported missing)
 * was added with the Strategic checkbox inline beside it.
 * The Date Range layout (chips left, From / To right) is unchanged.
 *
 * Client re-test (30 Sep 2026): Supplier Type moves to the Classification
 * section, where the supplied diagram places it (Supplier type · Commodity
 * group · Commodity type). The Strategic checkbox stays in Supplier, as drawn.
 */
import { CalendarRange, Building2, Users, Layers, FileText } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection, FilterGrid, FilterField, filterControlClass } from '../../../filters/FilterSection';
import { countryDisplayNames } from '../../../dashboard/dashboardFilterUtils';
import {
  ASN_STATUS_OPTIONS,
  ASN_COURIER_OPTIONS,
  ASN_COURIER_STATUS_OPTIONS,
  ASN_SUPPLIER_TYPE_OPTIONS,
  ASN_QUICK_SELECT_CHIPS,
  type ASNFilterOption,
} from './asnFilterOptions';
import { useFilterVisibility } from '../../../../hooks/useFilterVisibility';

/**
 * CR-6092: a 'created' ASN is a supplier-side draft the customer / helix list
 * never shows (the status tabs omit it for the same reason), so the customer
 * layout does not offer a status that can only return an empty list.
 */
const CUSTOMER_STATUS_OPTIONS = ASN_STATUS_OPTIONS.filter((o) => o.value !== 'created');

/** Horizontal divider with padding so it doesn't touch card edges (as on Deliveries). */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

export interface ASNFilterSectionsProps {
  filters: Record<string, string>;
  textInputs: Record<string, string>;
  siteOptions: ASNFilterOption[];
  warehouseOptions: ASNFilterOption[];
  supplierCountryOptions: ASNFilterOption[];
  supplierGroupOptions: ASNFilterOption[];
  /**
   * CR-6441 re-test (7 Sep): supplier sites already narrowed by Site /
   * Supplier Country / Supplier Group in `useASNFilterCascades`. Rendered as
   * a searchable select so those cascades visibly restrict the list, as on
   * the supplier layout; the value is still the name the
   * `supplier__name__icontains` param sends.
   */
  supplierSiteOptions?: ASNFilterOption[];
  commodityGroupOptions: ASNFilterOption[];
  commodityTypeOptions: ASNFilterOption[];
  /** CR-6449: Currently selected quick date range chip value */
  quickSelectValue: string;
  onFilterChange: (key: string, value: string) => void;
  /** CR-6449: Quick select date range chip change callback */
  onQuickSelectChange: (value: string) => void;
  /** Enter in a text field applies the filters. */
  onApply?: () => void;
  /** CR-6441: Division options for cascade filtering */
  divisionOptions?: ASNFilterOption[];
  /** CR-6441: Site country options for cascade filtering */
  siteCountries?: string[];
  /** CR-6160: Whether ASN logistics tracking module is enabled - gates courier status filter */
  trackingEnabled?: boolean;
  /** CR-6441 / CR-6160: Courier Delivery Status choices as served by the backend. */
  courierStatusOptions?: ASNFilterOption[];
}

export function ASNFilterSections({
  filters,
  textInputs,
  siteOptions,
  warehouseOptions,
  supplierCountryOptions,
  supplierGroupOptions,
  supplierSiteOptions = [],
  commodityGroupOptions,
  commodityTypeOptions,
  quickSelectValue,
  onFilterChange,
  onQuickSelectChange,
  onApply,
  divisionOptions = [],
  siteCountries = [],
  trackingEnabled = false,
  courierStatusOptions = ASN_COURIER_STATUS_OPTIONS,
}: ASNFilterSectionsProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  /** Reusable text input field - reduces duplicate code */
  const textField = (label: string, key: string, placeholder: string, useTextInputs = true) => (
    <FilterField label={label}>
      <input
        type="text"
        placeholder={placeholder}
        value={useTextInputs ? (textInputs[key] || '') : (filters[key] || '')}
        onChange={(e) => onFilterChange(key, e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onApply?.(); }}
        className={filterControlClass}
      />
    </FilterField>
  );

  /** Reusable select field - reduces duplicate code */
  const selectField = (
    label: string,
    key: string,
    options: ASNFilterOption[],
    placeholder = 'All',
  ) => (
    <FilterField label={label}>
      <select
        value={filters[key] || ''}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={filterControlClass}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </FilterField>
  );

  /** Reusable searchable select field */
  const searchableSelectField = (label: string, key: string, options: ASNFilterOption[], placeholder: string) => (
    <FilterField label={label}>
      <SearchableSelect
        options={options}
        value={filters[key] || ''}
        onChange={(val) => onFilterChange(key, val)}
        placeholder={placeholder}
      />
    </FilterField>
  );

  /** CR-6441: Quick select chip styling - matches Dashboard design */
  const chipBaseCls = 'px-4 py-2 text-sm font-medium border rounded-md transition-colors';
  const chipActiveCls = 'border-primary bg-primary/10 text-primary hover:bg-primary/20';
  const chipInactiveCls = 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary';

  /** Quick select chip button */
  const QuickSelectChip = ({ value, label }: { value: string; label: string }) => (
    <button
      type="button"
      onClick={() => onQuickSelectChange(value)}
      className={`${chipBaseCls} ${quickSelectValue === value ? chipActiveCls : chipInactiveCls}`}
    >
      {label}
    </button>
  );

  const dateField = (label: string, key: 'dateFrom' | 'dateTo') => (
    <FilterField label={label}>
      <input
        type="date"
        value={filters[key] || ''}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={filterControlClass}
      />
    </FilterField>
  );

  // Customer View — Dashboard-style shaded section headers, Deliveries-style
  // inset dividers between sections (the shared section's own edge-to-edge
  // border is suppressed so the two never stack).
  return (
    <div className="bg-card [&>section]:border-t-0">
      <SectionDivider />

      {/* DATE RANGE section - CR-6441: Layout matches Dashboard */}
      <FilterSection title="Date Range" icon={CalendarRange}>
        <div className="flex flex-wrap items-start justify-between gap-6 items-center">
          <div className="flex flex-wrap gap-2 max-w-2xl">
            {ASN_QUICK_SELECT_CHIPS.map(({ value, label }) => (
              <QuickSelectChip key={value} value={value} label={label} />
            ))}
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="w-44">{dateField('From Date', 'dateFrom')}</div>
            <div className="w-44">{dateField('To Date', 'dateTo')}</div>
          </div>
        </div>
      </FilterSection>

      <SectionDivider />

      {/* COMPANY — Division, Country, Site, Warehouse per diagram */}
      <FilterSection title="Company" icon={Building2}>
        <FilterGrid>
          {fv.division && selectField('Division', 'division', divisionOptions, 'All Divisions')}
          {fv.companyCountry && (
          <FilterField label="Country">
            <select
              value={filters['companyCountry'] || ''}
              onChange={(e) => onFilterChange('companyCountry', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Countries</option>
              {siteCountries.map((c) => (
                <option key={c} value={c}>{countryDisplayNames[c] || c}</option>
              ))}
            </select>
          </FilterField>
          )}
          {searchableSelectField('Site', 'site', siteOptions, 'All Sites')}
          {fv.warehouse && searchableSelectField('Warehouse', 'warehouse', warehouseOptions, 'All Warehouses')}
        </FilterGrid>
      </FilterSection>

      <SectionDivider />

      {/* SUPPLIER — Group, Country, Site, Code, Type + Strategic-only */}
      <FilterSection title="Supplier" icon={Users}>
        <FilterGrid>
          {searchableSelectField('Supplier Group', 'supplierGroup', supplierGroupOptions, 'All Supplier Groups')}
          {searchableSelectField('Supplier Country', 'supplierCountry', supplierCountryOptions, 'All Countries')}
          {/* Client re-test (7 Sep): a text box gave Site / Supplier Country /
              Supplier Group nothing to narrow. Now the cascade-fed select
              the supplier layout uses; typing still searches the list. */}
          {searchableSelectField('Supplier Site', 'supplier', supplierSiteOptions, 'All Supplier Sites')}          
          {fv.supplierCode && textField('Supplier Code', 'supplierCode', 'Enter supplier code', false)}
          {/* Client review: the checkbox sits in the grid, aligned with the
              control row (as on PO Lines). Supplier Type used to stand beside
              it; the 30 Sep re-test moved that to Classification per diagram. */}
          <div className="flex items-center pt-7">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters['strategic'] === 'yes'}
                onChange={(e) => onFilterChange('strategic', e.target.checked ? 'yes' : '')}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium">Strategic suppliers only</span>
            </label>
          </div>
        </FilterGrid>
      </FilterSection>

      {/* CR-4802: the whole group is tenant-gated; drop the heading when nothing is shown. */}
      {(fv.commodity || fv.supplierType) && (<>
        <SectionDivider />

        {/* CLASSIFICATION — diagram order: Supplier Type · Commodity Group · Commodity Type */}
        <FilterSection title="Classification" icon={Layers}>
          <FilterGrid>
            {/* Client test feedback: Supplier Type was missing (added under
                Supplier); client re-test 30 Sep: it belongs here per diagram.
                Internal / external is relative to the customer organisation
                in scope (backend supplier_type). */}
            {fv.supplierType && selectField('Supplier Type', 'supplierType', ASN_SUPPLIER_TYPE_OPTIONS, 'All Types')}
            {fv.commodity && selectField('Commodity Group', 'commodityGroup', commodityGroupOptions, 'All Commodity Groups')}
            {fv.commodity && selectField('Commodity Type', 'commodityType', commodityTypeOptions, 'All Commodity Types')}
          </FilterGrid>
        </FilterSection>
      </>)}

      <SectionDivider />

      {/* ORDER DETAILS — CR-6441: Buyer ID, Part Number, Description per diagram */}
      <FilterSection title="Order Details" icon={FileText}>
        <FilterGrid>
          {textField('ASN Number', 'asnNumber', 'Enter ASN number...')}
          {selectField('ASN Status', 'status', CUSTOMER_STATUS_OPTIONS, 'All Statuses')}
          {textField('PO Number', 'poNumber', 'Enter PO number...')}
          {textField('PO Line Number', 'poLineNumber', 'Enter line number...')}
          {textField('Buyer ID', 'buyerId', 'Enter buyer ID...', false)}
          {textField('Part Number', 'partNumber', 'Enter part number...')}
          {textField('Description', 'description', 'Enter description...')}
          {selectField('Courier', 'courier', ASN_COURIER_OPTIONS, 'All Couriers')}
          {textField('Tracking Number', 'trackingNumber', 'Enter tracking number...')}
          {/* CR-6160: Courier status filter only available when logistics tracking module is enabled */}
          {trackingEnabled && selectField('Courier Delivery Status', 'courierDeliveryStatus', courierStatusOptions, 'All Statuses')}
        </FilterGrid>
      </FilterSection>
    </div>
  );
}
